import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { clamp, dampingFor, pickSnap, prefersReducedMotion, rubberband, runSpring } from "./motion";
import "./sheet.css";

const SCRIM_MAX = 0.42;

// Bottom sheet that follows the finger, carries a flick's momentum and can be grabbed mid-flight.
// `snaps` are the visible fractions of the sheet it may rest at (1 = fully open). `header` is part of
// the drag area; mark clickable things inside it with data-sheet-nodrag.
export default function Sheet({ open, onClose, label, snaps = [1], initialSnap = 0, fill = false, header = null, children }) {
  const sheetRef = useRef(null);
  const scrimRef = useRef(null);
  const posRef = useRef(0);
  const heightRef = useRef(0);
  const cancelRef = useRef(null);
  const dragRef = useRef(null);
  const snapsRef = useRef(snaps);
  snapsRef.current = snaps;
  const [mounted, setMounted] = useState(open);
  // Part of a half-open sheet sits below the screen. The body keeps that much room at its end, so it can still be
  // scrolled and the last items are not stuck in the hidden part.
  const [hiddenPx, setHiddenPx] = useState(0);

  const offsets = useCallback((h) => snapsRef.current.map((f) => Math.round(h * (1 - f))), []);

  const apply = useCallback((y) => {
    posRef.current = y;
    const h = heightRef.current || 1;
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${y}px)`;
    if (scrimRef.current) scrimRef.current.style.opacity = String(clamp(1 - y / h, 0, 1) * SCRIM_MAX);
  }, []);

  const animateTo = useCallback(
    (target, velocity = 0, done) => {
      if (cancelRef.current) cancelRef.current();
      cancelRef.current = null;
      if (target < heightRef.current) setHiddenPx(target);
      if (prefersReducedMotion()) {
        apply(target);
        if (done) done();
        return;
      }
      cancelRef.current = runSpring({
        from: posRef.current,
        to: target,
        velocity,
        zeta: dampingFor(velocity),
        response: 0.35,
        onUpdate: (x) => apply(x),
        onDone: () => {
          cancelRef.current = null;
          if (done) done();
        },
      });
    },
    [apply]
  );

  useLayoutEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useLayoutEffect(() => {
    if (!mounted || !sheetRef.current) return;
    heightRef.current = sheetRef.current.offsetHeight;
    const h = heightRef.current;
    if (open) {
      apply(h);
      animateTo(offsets(h)[initialSnap] ?? 0, 0);
      sheetRef.current.focus({ preventScroll: true });
    } else {
      animateTo(h, 0, () => setMounted(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, open]);

  useEffect(() => () => cancelRef.current && cancelRef.current(), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onPointerDown = (e) => {
    if (e.target.closest("[data-sheet-nodrag]")) return;
    if (cancelRef.current) {
      cancelRef.current();
      cancelRef.current = null;
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* synthetic pointers cannot be captured */
    }
    dragRef.current = { startY: e.clientY, startPos: posRef.current, samples: [[performance.now(), e.clientY]] };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const h = heightRef.current;
    const top = Math.min(...offsets(h));
    const raw = d.startPos + (e.clientY - d.startY);
    apply(raw < top ? top - rubberband(top - raw, h) : Math.min(raw, h));
    const now = performance.now();
    d.samples.push([now, e.clientY]);
    while (d.samples.length > 2 && now - d.samples[0][0] > 100) d.samples.shift();
  };

  const endDrag = () => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    const first = d.samples[0];
    const last = d.samples[d.samples.length - 1];
    const dt = (last[0] - first[0]) / 1000;
    const velocity = dt > 0 ? (last[1] - first[1]) / dt : 0;
    const h = heightRef.current;
    const target = pickSnap(posRef.current, velocity, [...offsets(h), h]);
    animateTo(target, velocity, target >= h ? onClose : undefined);
  };

  if (!mounted) return null;

  return (
    <div className="rg-sheet-root">
      <div ref={scrimRef} className="rg-scrim" data-testid="sheet-scrim" onClick={onClose} />
      <div
        ref={sheetRef}
        className={`rg-sheet${fill ? " rg-sheet--fill" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        <div
          className="rg-sheet-drag"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <span className="rg-grab" aria-hidden="true" />
          {header}
        </div>
        <div className="rg-sheet-body" style={hiddenPx ? { paddingBottom: hiddenPx } : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}
